/**
 * Client billing run — the monthly, client-wide invoicing review.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invoiceApi } from '@/lib/api/invoices.api';
import { queryKeys } from '../query-keys';
import type { BillingRunPreview } from '@scrambl/shared';

export interface ClientBillingRunParams {
  periodStart?: string;
  periodEnd?: string;
  projectIds?: string[];
}

export const clientBillingRunQueryKey = (
  entityId: string,
  params: ClientBillingRunParams,
) => [...queryKeys.invoices.all, 'billing-run', entityId, params] as const;

export function useClientBillingRunQuery(
  entityId: string,
  params: ClientBillingRunParams,
  options: { enabled?: boolean } = {},
) {
  return useQuery<BillingRunPreview>({
    queryKey: clientBillingRunQueryKey(entityId, params),
    queryFn: () => invoiceApi.getClientBillingRunPreview(entityId, params),
    enabled: (options.enabled ?? true) && !!entityId,
    // Money that may already have been claimed by another user's run — never
    // serve it from a stale cache.
    staleTime: 0,
  });
}

/** Invalidate every period/project variant of one client's billing run. */
export function useInvalidateClientBillingRun() {
  const queryClient = useQueryClient();
  return (entityId: string) =>
    queryClient.invalidateQueries({
      queryKey: [...queryKeys.invoices.all, 'billing-run', entityId],
    });
}
